import {TrustAccountGuestRole, TrustAccountMemberRole} from '@sollapay/enums';
import {Typography, cn} from '@sollapay/ui';
import {Avatar, Dot} from '@sollapay/ui/components';
import {formatDate} from '@sollapay/utils';
import {useTranslation} from 'react-i18next';

import {getInitials} from '@/utils';

import {ShareRoleMenu} from './ShareRoleMenu';
import {ShareRowConfirm} from './ShareRowConfirm';
import {useShareRowStep} from '../hooks';
import {getGuestDisplayName} from '../utils';

import type {ShareRoleMenuAction, ShareRoleMenuOption} from './ShareRoleMenu';
import type {TrustAccountGuestDTO} from '@sollapay/types';
import type {TFunction} from 'i18next';
import type {FC} from 'react';

export interface ShareGuestRowProps {
  guest: TrustAccountGuestDTO;
  readOnly: boolean;
  onRemove: (userId: string) => Promise<void>;
  onResend: (userId: string) => Promise<void>;
}

/** One guest with their status; removing access or cancelling an invitation is confirmed inside the row. */
export const ShareGuestRow: FC<ShareGuestRowProps> = ({guest, readOnly, onRemove, onResend}) => {
  const {t} = useTranslation('trustAccounts');
  const {step, setStep, isBusy, settle} = useShareRowStep<'confirm'>();
  const {userId} = guest;
  const displayName = getGuestDisplayName(guest);
  const isPending = guest.status === 'pending';
  const viewerOption = getGuestViewerOption(t);
  const actions = getGuestActions({
    isPending,
    t,
    onResend: () => void onResend(userId),
    onConfirm: () => setStep('confirm')
  });

  return (
    <li data-slot="share-guest-row" className="flex flex-col gap-2 py-2">
      <div className="flex items-center gap-3">
        <ShareGuestIdentity guest={guest} />
        <ShareRoleMenu
          label={isPending ? t('share.guestRow.inviteSent') : viewerOption.label}
          personName={displayName}
          value={viewerOption.value}
          options={[viewerOption]}
          actions={actions}
          readOnly={readOnly}
        />
      </div>
      {step === 'confirm' && (
        <ShareRowConfirm
          message={t(isPending ? 'share.guestRow.confirmCancel' : 'share.guestRow.confirmRemove', {
            guest: displayName
          })}
          confirmLabel={t(
            isPending ? 'share.guestRow.confirmCancelCta' : 'share.guestRow.confirmRemoveCta'
          )}
          backLabel={t('share.guestRow.back')}
          isBusy={isBusy}
          onConfirm={() =>
            void settle(async () => {
              await onRemove(userId);
              return true;
            })
          }
          onCancel={() => setStep(null)}
        />
      )}
    </li>
  );
};

const ShareGuestIdentity: FC<{guest: TrustAccountGuestDTO}> = ({guest}) => {
  const {t} = useTranslation('trustAccounts');
  const {label, dotClassName} = getGuestStatus(guest, t);
  const {name, email, userId} = guest;

  return (
    <>
      <Avatar initials={name ? getInitials(name) : email} seed={userId} size="default" noShadow />
      <div className="flex min-w-0 flex-1 flex-col">
        {/* An address is LTR so a long one clips at its end; `self-start` keeps it at the row's start side. */}
        <Typography
          size="sm"
          weight="semibold"
          dir={name ? undefined : 'ltr'}
          className={cn(!name && 'max-w-full self-start')}
        >
          {getGuestDisplayName(guest)}
        </Typography>
        <span className="flex items-center gap-1.5">
          <Dot colorClassName={dotClassName} />
          <Typography size="xs" color="tertiary">
            {label}
          </Typography>
        </span>
      </div>
    </>
  );
};

/** A pending invitation can be resent or cancelled; a joined guest's access can be removed. */
function getGuestActions(params: {
  isPending: boolean;
  t: TFunction;
  onResend: () => void;
  onConfirm: () => void;
}): ShareRoleMenuAction[] {
  const {isPending, t, onResend, onConfirm} = params;
  return isPending
    ? [
        {label: t('share.guestRow.resend'), onClick: onResend},
        {label: t('share.guestRow.cancel'), onClick: onConfirm, destructive: true}
      ]
    : [{label: t('share.guestRow.remove'), onClick: onConfirm, destructive: true}];
}

/** Guests are viewers only, so their menu has the one role, worded like a member viewer's. */
function getGuestViewerOption(t: TFunction): ShareRoleMenuOption {
  const viewer = TrustAccountMemberRole.VIEWER;
  return {
    value: TrustAccountGuestRole.VIEWER,
    label: t(`share.roles.${viewer}.label`),
    description: t(`share.roles.${viewer}.description`)
  };
}

function getGuestStatus(
  guest: TrustAccountGuestDTO,
  t: TFunction
): {label: string; dotClassName: string} {
  if (guest.status === 'active') {
    return {
      label: t('share.guestRow.joined', {date: guest.joinedAt ? formatDate(guest.joinedAt) : ''}),
      dotClassName: 'bg-indication-success'
    };
  }
  const {inviteExpiresAt} = guest;
  if (inviteExpiresAt && new Date(inviteExpiresAt).getTime() < Date.now()) {
    return {
      label: t('share.guestRow.expired', {date: formatDate(inviteExpiresAt)}),
      dotClassName: 'bg-indication-error'
    };
  }
  return {
    label: t('share.guestRow.pending', {date: inviteExpiresAt ? formatDate(inviteExpiresAt) : ''}),
    dotClassName: 'bg-indication-warning'
  };
}
